<script setup lang="ts">
import { ref, computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import { Button } from '@/Components/ui/button'
import { Checkbox } from '@/Components/ui/checkbox'
import { Alert, AlertDescription } from '@/Components/ui/alert'
import { AlertCircle, CheckCircle2, Eye, EyeOff, BookOpen } from 'lucide-vue-next'
import LegalModal from '@/Components/Modals/LegalModal.vue'
import StudentTermsContent from '@/Components/Legal/StudentTermsContent.vue'
import PrivacyPolicyContent from '@/Components/Legal/PrivacyPolicyContent.vue'

interface Props {
  showTutorBanner?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showTutorBanner: true,
})

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'switch-to', view: 'login' | 'forgot-password'): void
}>()

const form = ref({
  fullname: '',
  email: '',
  password: '',
  password_confirmation: '',
  terms: false,
})

const showPassword = ref(false)
const showConfirmPassword = ref(false)
const isSubmitting = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')
const successMessage = ref('')
const showTermsModal = ref(false)
const showPrivacyModal = ref(false)

const hasErrors = computed(() => Object.keys(errors.value).length > 0 || !!generalError.value)

async function submit() {
  if (!form.value.terms) {
    errors.value = { terms: ['You must agree to the terms and conditions.'] }
    return
  }

  isSubmitting.value = true
  errors.value = {}
  generalError.value = ''

  try {
    await window.axios.post('/signup', form.value)
    successMessage.value = 'Account created successfully! Redirecting...'

    setTimeout(() => {
      emit('success')
      // Reload to get fresh auth state
      window.location.reload()
    }, 500)
  } catch (error: any) {
    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors
    } else if (error.response?.data?.message) {
      generalError.value = error.response.data.message
    } else {
      generalError.value = 'An error occurred. Please try again.'
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <div class="space-y-5">
    <!-- Header -->
    <div class="text-center">
      <h2 class="text-2xl font-bold text-gray-900 mb-2">Create an Account</h2>
      <p class="text-gray-600">Join thousands of students learning with Bindle</p>
    </div>

    <!-- Become a tutor banner -->
    <div v-if="showTutorBanner" class="p-3 bg-blue-50 rounded-lg flex items-start space-x-3">
      <BookOpen class="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
      <div class="text-sm text-blue-900">
        Want to become a tutor?
        <Link href="/become-a-tutor" class="font-semibold text-blue-700 hover:text-blue-800 underline">
          Start here
        </Link>
      </div>
    </div>

    <form @submit.prevent="submit" class="space-y-4">
      <!-- Error Alert -->
      <Alert v-if="hasErrors" variant="destructive">
        <AlertCircle class="h-4 w-4" />
        <AlertDescription>
          {{ generalError || 'Please correct the errors below.' }}
        </AlertDescription>
      </Alert>

      <!-- Success Alert -->
      <Alert v-if="successMessage" class="border-green-500 text-green-700 [&>svg]:text-green-700">
        <CheckCircle2 class="h-4 w-4" />
        <AlertDescription>{{ successMessage }}</AlertDescription>
      </Alert>

      <!-- Full Name Field -->
      <div>
        <label for="signup-fullname" class="block text-sm font-semibold text-gray-700 mb-2">
          Full Name <span class="text-red-500">*</span>
        </label>
        <input
          id="signup-fullname"
          type="text"
          placeholder="John Doe"
          v-model="form.fullname"
          required
          class="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all text-gray-900"
          :class="errors.fullname ? 'border-red-500' : 'border-gray-200'"
        />
        <p v-if="errors.fullname" class="text-sm text-red-500 mt-1">{{ errors.fullname[0] }}</p>
      </div>

      <!-- Email Field -->
      <div>
        <label for="signup-email" class="block text-sm font-semibold text-gray-700 mb-2">
          Email <span class="text-red-500">*</span>
        </label>
        <input
          id="signup-email"
          type="email"
          placeholder="m@example.com"
          v-model="form.email"
          required
          class="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all text-gray-900"
          :class="errors.email ? 'border-red-500' : 'border-gray-200'"
        />
        <p v-if="errors.email" class="text-sm text-red-500 mt-1">{{ errors.email[0] }}</p>
      </div>

      <!-- Password Field -->
      <div>
        <label for="signup-password" class="block text-sm font-semibold text-gray-700 mb-2">
          Password <span class="text-red-500">*</span>
        </label>
        <div class="relative">
          <input
            id="signup-password"
            :type="showPassword ? 'text' : 'password'"
            v-model="form.password"
            required
            class="w-full px-4 py-3 pr-12 border-2 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all text-gray-900"
            :class="errors.password ? 'border-red-500' : 'border-gray-200'"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <EyeOff v-if="showPassword" class="w-5 h-5" />
            <Eye v-else class="w-5 h-5" />
          </button>
        </div>
        <p v-if="errors.password" class="text-sm text-red-500 mt-1">{{ errors.password[0] }}</p>
      </div>

      <!-- Confirm Password Field -->
      <div>
        <label for="signup-password-confirm" class="block text-sm font-semibold text-gray-700 mb-2">
          Confirm Password <span class="text-red-500">*</span>
        </label>
        <div class="relative">
          <input
            id="signup-password-confirm"
            :type="showConfirmPassword ? 'text' : 'password'"
            v-model="form.password_confirmation"
            required
            class="w-full px-4 py-3 pr-12 border-2 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all text-gray-900"
            :class="errors.password_confirmation ? 'border-red-500' : 'border-gray-200'"
          />
          <button
            type="button"
            @click="showConfirmPassword = !showConfirmPassword"
            class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <EyeOff v-if="showConfirmPassword" class="w-5 h-5" />
            <Eye v-else class="w-5 h-5" />
          </button>
        </div>
        <p v-if="errors.password_confirmation" class="text-sm text-red-500 mt-1">{{ errors.password_confirmation[0] }}</p>
      </div>

      <!-- Terms Checkbox -->
      <div class="flex items-start space-x-2">
        <Checkbox
          id="signup-terms"
          :checked="form.terms"
          @update:checked="form.terms = $event"
        />
        <label
          for="signup-terms"
          class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mt-0.5"
          :class="{'text-red-500': errors.terms}"
        >
          I agree to the
          <button type="button" @click.prevent="showTermsModal = true" class="text-blue-600 hover:underline">Terms of Service</button>
          and
          <button type="button" @click.prevent="showPrivacyModal = true" class="text-blue-600 hover:underline">Privacy Policy</button>
        </label>
      </div>
      <p v-if="errors.terms" class="text-sm text-red-500">{{ errors.terms[0] }}</p>

      <Button
        type="submit"
        class="w-full bg-green-600 hover:bg-green-700 h-auto px-5 py-3.5 text-base font-semibold rounded-xl"
        :disabled="isSubmitting"
      >
        {{ isSubmitting ? 'Creating Account...' : 'Create Account' }}
      </Button>
    </form>

    <!-- Footer -->
    <div class="text-center text-sm text-gray-600">
      Already have an account?
      <button
        type="button"
        @click="$emit('switch-to', 'login')"
        class="font-semibold text-green-600 hover:text-green-500"
      >
        Sign in
      </button>
    </div>

    <!-- Legal Modals -->
    <LegalModal :show="showTermsModal" title="Student Terms & Conditions" @close="showTermsModal = false">
      <StudentTermsContent />
    </LegalModal>

    <LegalModal :show="showPrivacyModal" title="Privacy Policy" @close="showPrivacyModal = false">
      <PrivacyPolicyContent />
    </LegalModal>
  </div>
</template>
